/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'react',
	'@ba/ba-ui-carbon-toolkit',
	'./PropertiesContainer',
	'../../lib/@waca/carbon-icons'
], function (
	React,
	Toolkit,
	PropertiesContainer,
	CarbonIcons
) {
	const {
		Label,
		AccordionItem
	} = Toolkit;

	const {
		Reset16
	} = CarbonIcons;

	const skipSections = ['Separator'];

	// Container controls are the layout controls of type wrapper of other controls.
	// They don't have propertyId and need to have a callback to the method makeSectionItem in this class to create their wrapped controls.
	const containerControls = ['SingleLineLinks', 'Split'];

	// VIPR property layouts related to fonts have the layoutId = prefix + propertyId
	// This constant lists all these prefixes that need to be substracted to get the propertyId
	const property_prefixes = [ 'font.xdashboardx.', 'fontSize.xdashboardx.'];

	const SUPPORTED_ICONS = {
		Reset16
	};

	const shouldSkipSpec = (spec) => {
		if (skipSections.indexOf(spec.type) > -1) {
			return true;
		}
		return false;
	};

	class V2PropertyControlsFactory {

		constructor({ controls, dashboard }) {
			this._dashboard = dashboard;
			this._controls = controls;
			this._logger = this._dashboard.getFeature('Logger');
		}

		_makeBanner(banner) {
			return (
				<Label
					className="properties--title"
					label={banner.value}
				/>
			);
		}

		// This is for backward compatibility, new Carbon design do not have groups, only sections and its list of items 
		_collectOrderedGroups(layouts) {
			const originalGroups = layouts.filter((layout) => layout && layout.type === 'Group');
			const orderedGroups = [];
			if (originalGroups.length) {
				for (let i = 0; i < originalGroups.length; i++) {
					let group = originalGroups[i];
					if (group.items && group.items.length) {
						// return only the group that has items
						if (group.position === undefined) {
							// if there is no explicit position specified, set position to 10;
							// that means the provider doesn't care about order
							group.position = 10;
						}
						orderedGroups.push(group);
					}
				}
				orderedGroups.sort((a,b) => a.position - b.position);
			}
			return orderedGroups;
		}

		_makeCollapsibleSection(section) {
			const { items, label } = section;
			const result = [];
			result.push(
				<Label className='properties--controls--sectionLabel' label={label}/>
			);
			(items || []).forEach((item, key) => {
				if (!shouldSkipSpec(item)) {
					const sectionItem = this.makeSectionItem(item, key);
					if (sectionItem) {
						result.push(sectionItem);
					}
				}
			});
			return result;
		}

		_makeSection(section) {
			const { items, label } = section;
			let sectionItems = [];
			(items || []).forEach((item, key) => {
				if (!shouldSkipSpec(item)) {
					if (item.type === 'Section' && item.collapsible === false) {
						// Case nested sections, section is not expandable/collapsible, generate a Label for it and convert its children
						sectionItems = sectionItems.concat(this._makeCollapsibleSection(item));
					} else {
						const sectionItem = this.makeSectionItem(item, key);
						if (sectionItem) {
							sectionItems.push(sectionItem);
						}
					}
				}
			});
			return (
				<AccordionItem
					className='property--section--label'
					itemName={label}
					open={false}
					icon= 'right'
				>
					{sectionItems}
				</AccordionItem>
			);
		}

		_getProperty(id) {
			let property = this._metadata.find(item => item.id === id);
			if (!property) {
				// try to see if id is layout Id that contains prefix 
				const propertyId = this._extractId(id);
				if (propertyId !== id) {
					property = this._metadata.find(item => item.id === propertyId);
				}
			}
			return property;
		}

		/**
		 * Some layout items have id = prefix + propertyId (e.g. font-related properties for VIPR vis)
		 * This function substract the prefix in this case to return the property id. 
		 */
		_extractId(originalId) {
			let propertyId = originalId;
			for (let i = 0; i < property_prefixes.length; ++i) {
				if (originalId.indexOf(property_prefixes[i]) === 0) {
					const prefixLength = property_prefixes[i].length;
					propertyId = originalId.substring(prefixLength, originalId.length);
					break;
				}
			}
			return propertyId;
		}

		_parseItemStyle(item) {
			const result = {};
			let itemStyle;
			if (item.style) {
				itemStyle = item.style;
			} else if (item.editor && item.editor.uiControl && item.editor.uiControl.style) {
				itemStyle = item.editor.uiControl.style;
			} else {
				itemStyle = '';
			}
			itemStyle
				.split(';')
				.filter(item => !!item)
				.forEach(item => {
					const [ key, value ] = item.split(':');
					result[key.trim()] = value.trim();
				});

			return result;
		}

		getIcon(iconName) {
			const iconFeature =  this._dashboard.getFeature('Icons');
			return iconFeature.getIcon(iconName);

		}

		makeSectionItem(layoutItem, key) {
			const controlType = this._getControlType(layoutItem);
			const style = this._parseItemStyle(layoutItem);
			const PropertyControl = this._controls[controlType];

			if (PropertyControl) {
				const controlClassName = `properties--controls properties--controls--${controlType}`;
				if (containerControls.indexOf(controlType) > -1) {
					// if the layout item is of container type (has many children items)
					const makeSectionItemCb = this.makeSectionItem.bind(this);
					const getIconCb = this.getIcon.bind(this);
					return (
						<PropertyControl
							item={layoutItem}
							className={controlClassName}
							makeSectionItemCb={makeSectionItemCb}
							supportedIcons={SUPPORTED_ICONS}
							getIconCb={getIconCb}
							logger={this._logger}
						/>
					);
				}
				// if the layout item is the leaf item (associated with 1 property) 
				const property = this._getProperty(layoutItem.id);
				if (!property) {
					this._logger.warn('property not found in metadata: ',  layoutItem.id);
					return null;
				}
				return (
					<PropertyControl
						key={key}
						item={property}
						style={style}
						className={controlClassName}
					/>
				);
			} else {
				// TODO: this is the temporary solution in waiting for implementing all the control
				// components; when the implementation of all the components is done. this will no longer be necessary
				return (
					<div>
						<span>id: {layoutItem.id ? layoutItem.id : 'id'}</span>
						<span>|</span>
						<span>type: {controlType}</span>
					</div>
				);
			}
		}

		/**
		 * Get the UIControl type from its relative module path
		 * Ex: module = '../ui/uiSlider'  => return 'uiSlider'
		 * @param {String} modulePath - path of the module
		 * @return {String} module name
		 */
		_getTypeFromModule(modulePath) {
			let type = modulePath;
			if (modulePath) {
				// search the last character '/'
				const lastSlashIndex = modulePath.lastIndexOf('/');
				if (lastSlashIndex > -1 && lastSlashIndex < modulePath.length - 1) {
					type = modulePath.substring(lastSlashIndex + 1, modulePath.length);
				}
			}
			return type;
		}

		_getControlType(item) {
			let type = item.type ? item.type : null;
			if (!type) {
				type = item.module && this._getTypeFromModule(item.module);
				if (!type) {
					const uiControl = item.editor && item.editor.uiControl;
					if (uiControl) {
						type = this._getControlType(uiControl);
					}
				}
				if (type) {
					item.type = type;
				}
			}
			return type;
		}

		_makeSections(layoutList) {
			const orderedGroups = this._collectOrderedGroups(layoutList);
			const sections = [];
			if (orderedGroups.length) {
				orderedGroups.forEach((group) => {
					if (group.items && group.items.length) {
						group.items.forEach(groupItem => {
							if (groupItem.type === 'Section') {
								sections.push(this._makeSection(groupItem));
							}
						});
					}
				});
			} else {
				layoutList.forEach(layout => {
					if (layout && layout.type === 'Section') {
						sections.push(this._makeSection(layout));
					}
				});
			}
			return sections;
		}

		makeControls(metadata, properties) {
			this._metadata = metadata;
			const bannerProp = properties.find(layout => layout && layout.type === 'Banner');
			const banner = bannerProp && this._makeBanner(bannerProp);
			const sections = this._makeSections(properties);
			return (
				<PropertiesContainer
					banner = { banner }
					sections = { sections }
				/>
			);
		}
	}

	return V2PropertyControlsFactory;
});